import { createElement, type ReactNode } from 'react';
import type { RowData, SortingFn, SortingState, Table } from '@tanstack/react-table';
import { describeSort, type SortKind } from './useSortedPaged';

// Shared AppTable helpers: ScenarioIQ's sort rules and a typed way for cells to read page state.

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    /**
     * Page state and handlers the cells need, read with `tableView`. Keeps the columns a stable
     * constant: TanStack renders each `cell` as a component, so columns rebuilt on every render
     * remount every cell (open dropdowns close, inputs lose focus).
     */
    view?: unknown;
  }
}

/**
 * `useReactTable` options for a table without pagination. TanStack resets the page index whenever
 * the row model is rebuilt, and that reset is a state update: with a `data` array that's new on
 * every render, the table would re-render forever (a click then makes it synchronous and freezes the page).
 */
export const unpaginated = { autoResetPageIndex: false } as const;

/** The `meta.view` a page passed to `useReactTable`, typed by the caller. */
export const tableView = <V, T = unknown>(table: Table<T>): V => table.options.meta?.view as V;

// Row type is irrelevant here (it only reads the column value), so it fits every table's columns.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const textSort: SortingFn<any> = (a, b, columnId) =>
  String(a.getValue(columnId)).localeCompare(String(b.getValue(columnId)), undefined, { numeric: true, sensitivity: 'base' });

/**
 * Column options that give an AppTable column ScenarioIQ's sort rules (the same as useSortedPaged):
 * empty values last, text A–Z first, numbers and dates highest/newest first.
 * The column's accessor must return `undefined` for an empty value, and a timestamp for a date.
 * Pair with `enableSortingRemoval: false` on the table so a second click flips instead of clearing.
 */
export const sortableAs = (kind: SortKind) => ({
  sortingFn: kind === 'text' ? textSort : ('basic' as const),
  sortDescFirst: kind !== 'text',
  sortUndefined: 'last' as const,
});

/** Empty strings and missing values as `undefined`, so `sortUndefined: 'last'` keeps them at the end. */
export const orUndefined = <V,>(value: V | null | undefined | ''): V | undefined =>
  value === null || value === undefined || value === '' ? undefined : value;

/** Timestamp for a date column, `undefined` when missing or invalid. */
export const timeOf = (value: string | Date | null | undefined): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const t = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(t) ? undefined : t;
};

/** "Sorted by date saved, newest first" for the current TanStack sort. */
export const sortLineFor = (sorting: SortingState, columns: Record<string, { label: string; kind: SortKind }>): string | null => {
  const active = sorting[0];
  const column = active && columns[active.id];
  return column ? describeSort(column.label, column.kind, active.desc ? 'desc' : 'asc') : null;
};

/** Right-aligned numeric column (logical `end`, as AppTable asks). */
export const alignEnd = { cellStyle: { textAlign: 'end' as const }, headerStyle: { textAlign: 'end' as const } };

/**
 * Header label for a right-aligned column. AppTable's header content is a full-width flex row, so the
 * `textAlign: 'end'` in `alignEnd` doesn't move the label; this pushes it to the end, before the sort icon.
 */
export const headerEnd = (label: ReactNode) => createElement('span', { className: 'flex-1 text-right' }, label);
